const frontendConfig = {
  // API Configuration
  api: {
    baseURL: process.env.REACT_APP_BASE_API || 'http://localhost:5000',
    apiPath: 'v1',
    timeout: 30000,
    retries: 3
  },

  // App Configuration
  app: {
    name: 'MailChimp',
    version: '1.0.0',
    environment: process.env.NODE_ENV || 'development',
    debug: process.env.REACT_APP_DEBUG === 'true'
  },

  // Authentication Configuration
  auth: {
    tokenKey: 'Token',
    userIdKey: 'UserId',
    userDataKey: 'UserData',
    userRoleKey: 'UserRole',
    refreshTokenKey: 'RefreshToken',
    tokenExpiryKey: 'TokenExpiry'
  },

  // Role Configuration
  roles: {
    default: 'Member',
    system: ['SuperAdmin', 'Admin', 'Member'],
    permissions: {
      SuperAdmin: {
        // View permissions
        canViewUsers: true,
        canViewTeamMembers: true,
        canViewInvitations: true,
        canViewTemplates: true,
        canViewSettings: true,
        canViewLibrary: true,
        canViewAnalytics: true,
        canViewActivity: true,
        canViewSystemSettings: true,
        canViewAllActivity: true,
        
        // Manage permissions
        canManageUsers: true,
        canInviteUsers: true,
        canInviteMember: true, // Permission for inviting members
        canCreateInvitation: true, // Permission for creating/sending invitations
        canEditInvitations: true, // Permission for editing invitations
        canCancelInvitations: true, // Permission for canceling invitations
        canEditTeamMembers: true, // Permission for editing team members
        canDeleteTeamMembers: true, // Permission for deleting team members permanently
        canDeleteUsers: true,
        canUpdateRoles: true,
        canManageTemplates: true,
        canManageSettings: true,
        canRemoveTeamMembers: true,
        canViewProjects: true,
        canCreateProjects: true,
        canEditProjects: true,
        canDeleteProjects: true,
        canManageLibrary: true,
        canManageSuperAdmins: true,
        canManageAllTeams: true,
        canAccessAdminPanel: true,
        canViewEmailCampaigns: true,
        canCreateEmailCampaigns: true,
        canEditEmailCampaigns: true,
        canDeleteEmailCampaigns: true,
        canSendEmailCampaigns: true,
        canViewImageCDN: true,
        canUploadImageCDN: true,
        canEditImageCDN: true,
        canDeleteImageCDN: true,
        canManageImageCDN: true,
        canViewLibrary: true,
        canUploadLibrary: true,
        canEditLibrary: true,
        canDeleteLibrary: true,
        canManageLibrary: true
      },
      Admin: {
        // View permissions
        canViewUsers: true,
        canViewTeamMembers: true,
        canViewInvitations: true,
        canViewTemplates: true,
        canViewSettings: false,
        canViewLibrary: true,
        canViewAnalytics: true,
        canViewActivity: true,
        canViewSystemSettings: false,
        canViewAllActivity: false,
        
        // Manage permissions
        canManageUsers: true,
        canInviteUsers: true,
        canInviteMember: true, // Permission for inviting members
        canCreateInvitation: true, // Permission for creating/sending invitations
        canEditInvitations: true, // Permission for editing invitations
        canCancelInvitations: false, // Permission for canceling invitations
        canEditTeamMembers: true, // Permission for editing team members
        canDeleteTeamMembers: false, // Permission for deleting team members permanently
        canDeleteUsers: false,
        canUpdateRoles: false,
        canManageTemplates: true,
        canManageSettings: false,
        canRemoveTeamMembers: true,
        canViewProjects: true,
        canCreateProjects: true,
        canEditProjects: true,
        canDeleteProjects: false,
        canManageLibrary: true,
        canManageSuperAdmins: false,
        canManageAllTeams: false,
        canAccessAdminPanel: false,
        canViewEmailCampaigns: true,
        canCreateEmailCampaigns: true,
        canEditEmailCampaigns: true,
        canDeleteEmailCampaigns: false,
        canSendEmailCampaigns: true,
        canViewImageCDN: true,
        canUploadImageCDN: true,
        canEditImageCDN: true,
        canDeleteImageCDN: false,
        canManageImageCDN: true,
        canViewLibrary: true,
        canUploadLibrary: true,
        canEditLibrary: true,
        canDeleteLibrary: false,
        canManageLibrary: true
      },
      Member: {
        // View permissions
        canViewUsers: false,
        canViewTeamMembers: false,
        canViewInvitations: false,
        canViewTemplates: true, // Members should be able to view templates
        canViewSettings: true, // Members should be able to access their settings
        canViewLibrary: true, // Members should be able to view library
        canViewAnalytics: true,
        canViewActivity: true,
        canViewSystemSettings: false,
        canViewAllActivity: false,
        
        // Manage permissions
        canManageUsers: false,
        canInviteUsers: false,
        canInviteMember: true, // Permission for inviting members
        canCreateInvitation: false, // Permission for creating/sending invitations
        canEditInvitations: false, // Permission for editing invitations
        canCancelInvitations: false, // Permission for canceling invitations
        canEditTeamMembers: false, // Permission for editing team members
        canDeleteTeamMembers: false, // Permission for deleting team members permanently
        canDeleteUsers: false,
        canUpdateRoles: false,
        canManageTemplates: false,
        canManageSettings: false,
        canRemoveTeamMembers: false,
        canViewProjects: true,
        canCreateProjects: true,
        canEditProjects: true,
        canDeleteProjects: false,
        canManageLibrary: false,
        canManageSuperAdmins: false,
        canManageAllTeams: false,
        canAccessAdminPanel: false,
        canViewEmailCampaigns: true,
        canCreateEmailCampaigns: true,
        canEditEmailCampaigns: false,
        canDeleteEmailCampaigns: false,
        canSendEmailCampaigns: false,
        canViewImageCDN: true,
        canUploadImageCDN: true,
        canEditImageCDN: false,
        canDeleteImageCDN: false,
        canManageImageCDN: false,
        canViewLibrary: true,
        canUploadLibrary: true,
        canEditLibrary: false,
        canDeleteLibrary: false,
        canManageLibrary: false
      }
    }
  },

  // UI Configuration
  ui: {
    theme: {
      primary: '#667eea',
      secondary: '#764ba2',
      success: '#38a169',
      warning: '#ed8936',
      error: '#e53e3e',
      info: '#3182ce'
    },
    sidebar: {
      width: 200,
      collapsedWidth: 60
    },
    pagination: {
      defaultPageSize: 10,
      maxPageSize: 100
    }
  },

  // Validation Configuration
  validation: {
    password: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: true
    },
    email: {
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    }
  },

  // File Upload Configuration
  upload: {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/csv'],
    maxFiles: 5
  },

  // Notification Configuration
  notifications: {
    toast: {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      newestOnTop: false,
      closeOnClick: true,
      rtl: false,
      pauseOnFocusLoss: true,
      draggable: true,
      pauseOnHover: true
    }
  },

  // Routes Configuration
  routes: {
    auth: '/auth',
    dashboard: '/',
    projects: '/',
    activity: '/activity',
    library: '/library',
    team: '/team',
    teamMembers: '/team-members',
    settings: '/settings',
    emailBuilder: '/email-builder',
    forgotPassword: '/forgotpassword',
    resetPassword: '/resetpassword',
    acceptInvitation: '/accept-invitation'
  },

  // Feature Flags
  features: {
    roleManagement: true,
    teamInvitations: true,
    emailTemplates: true,
    analytics: true,
    notifications: true,
    fileUpload: true,
    realTimeUpdates: false // Disabled by default to prevent excessive API calls
  }
};

// Helper functions
frontendConfig.isDevelopment = () => frontendConfig.app.environment === 'development';
frontendConfig.isProduction = () => frontendConfig.app.environment === 'production';
frontendConfig.isTest = () => frontendConfig.app.environment === 'test';

frontendConfig.getApiURL = (endpoint = '') => {
  return `${frontendConfig.api.baseURL}${endpoint}`;
};

frontendConfig.getRolePermissions = (roleName) => {
  // Convert role name to lowercase for case-insensitive lookup
  const normalizedRoleName = roleName ? roleName.toLowerCase() : frontendConfig.roles.default;
  return frontendConfig.roles.permissions[normalizedRoleName] || frontendConfig.roles.permissions[frontendConfig.roles.default];
};

frontendConfig.hasPermission = (userRole, permission) => {
  const permissions = frontendConfig.getRolePermissions(userRole);
  return permissions[permission] || false;
};

frontendConfig.getAuthToken = () => {
  return localStorage.getItem(frontendConfig.auth.tokenKey);
};

frontendConfig.setAuthToken = (token) => {
  localStorage.setItem(frontendConfig.auth.tokenKey, token);
};

frontendConfig.removeAuthToken = () => {
  localStorage.removeItem(frontendConfig.auth.tokenKey);
};

frontendConfig.getUserId = () => {
  return localStorage.getItem(frontendConfig.auth.userIdKey);
};

frontendConfig.setUserId = (userId) => {
  localStorage.setItem(frontendConfig.auth.userIdKey, userId);
};

frontendConfig.getUserRole = () => {
  return localStorage.getItem(frontendConfig.auth.userRoleKey) || frontendConfig.roles.default;
};

frontendConfig.setUserRole = (role) => {
  localStorage.setItem(frontendConfig.auth.userRoleKey, role);
};

frontendConfig.getUserData = () => {
  try {
    const userData = localStorage.getItem(frontendConfig.auth.userDataKey);
    if (userData) {
      return JSON.parse(userData);
    }
    
    // If localStorage is empty but user is authenticated, fetch from API
    if (frontendConfig.isAuthenticated()) {
      frontendConfig.refreshUserDataFromAPI();
      return null; // Return null for now, will be updated by refresh
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

frontendConfig.setUserData = (userData) => {
  // Store only essential user data for security and performance
  const essentialData = {
    UserId: userData.UserId,
    FirstName: userData.FirstName,
    LastName: userData.LastName,
    EmailAddress: userData.EmailAddress,
    profilePicture: userData.profilePicture,
    Role: userData.Role,
    CompanyName: userData.CompanyName
  };
  localStorage.setItem(frontendConfig.auth.userDataKey, JSON.stringify(essentialData));
};

// Function to refresh user data from API and store in localStorage
frontendConfig.refreshUserDataFromAPI = async () => {
  try {
    const userId = frontendConfig.getUserId();
    const token = frontendConfig.getAuthToken();
    
    if (!userId || !token) {
      return null;
    }
    const response = await fetch(`${frontendConfig.api.baseURL}/${frontendConfig.api.apiPath}/user/userdata?UserId=${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'id': userId,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.statusCode === 200 && data.data) {
        // Store essential data in localStorage
        frontendConfig.setUserData(data.data);
        
        // Dispatch custom event to notify components that user data has changed
        window.dispatchEvent(new CustomEvent('userDataRefreshed', { detail: data.data }));
        
        return data.data;
      }
    } else if (response.status === 401) {
      // Token expired or invalid
      frontendConfig.clearAuthData();
      window.location.href = '/auth';
      return null;
    }
  } catch (error) {
    console.error('Error refreshing user data from API:', error);
  }
  return null;
};

// Function to refresh user data from server
frontendConfig.refreshUserData = async () => {
  try {
    const userId = frontendConfig.getUserId();
    const token = frontendConfig.getAuthToken();
    
    if (!userId || !token) {
      return null;
    }
    const response = await fetch(`${frontendConfig.api.baseURL}/${frontendConfig.api.apiPath}/user/userdata?UserId=${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'id': userId,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.statusCode === 200 && data.data) {
        // Update localStorage with fresh data
        frontendConfig.setUserData(data.data);
        
        // Dispatch custom event to notify components that user data has changed
        window.dispatchEvent(new CustomEvent('userDataRefreshed', { detail: data.data }));
        
        return data.data;
      }
    } else if (response.status === 401) {
      // Token expired or invalid, but don't redirect automatically
      // Let the AxiosInstance interceptor handle this
      return null;
    }
  } catch (error) {
    console.error('Error refreshing user data:', error);
  }
  return null;
};

frontendConfig.clearAuthData = () => {
  // localStorage.removeItem(frontendConfig.auth.tokenKey);
  // localStorage.removeItem(frontendConfig.auth.userIdKey);
  // localStorage.removeItem(frontendConfig.auth.userDataKey);
  // localStorage.removeItem(frontendConfig.auth.userRoleKey);
  // localStorage.removeItem(frontendConfig.auth.refreshTokenKey);
  // localStorage.removeItem(frontendConfig.auth.tokenExpiryKey);
  localStorage.clear();
};

frontendConfig.isAuthenticated = () => {
  const token = frontendConfig.getAuthToken();
  const userId = frontendConfig.getUserId();
  return !!(token && userId);
};

frontendConfig.getTeamId = async () => {
  // Fetch teamId from API when needed instead of storing in localStorage
  try {
    const fullUserData = await frontendConfig.fetchFullUserData();
    return fullUserData?.teamId || null;
  } catch (error) {
    console.error('Error fetching teamId:', error);
    return null;
  }
};

// Function to fetch full user data from API when needed
frontendConfig.fetchFullUserData = async () => {
  try {
    const userId = frontendConfig.getUserId();
    const token = frontendConfig.getAuthToken();
    
    if (!userId || !token) {
      return null;
    }
    const response = await fetch(`${frontendConfig.api.baseURL}/${frontendConfig.api.apiPath}/user/userdata?UserId=${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'id': userId,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.statusCode === 200 && data.data) {
        return data.data;
      }
    } else if (response.status === 401) {
      return null;
    }
  } catch (error) {
    console.error('Error fetching full user data:', error);
  }
  return null;
};

// Global listener for localStorage changes
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === frontendConfig.auth.userDataKey && e.newValue === null) {
      // UserData was deleted from localStorage
      if (frontendConfig.isAuthenticated()) {
        frontendConfig.refreshUserDataFromAPI();
      }
    }
  });
  
  // Also check on page load if localStorage is empty but user is authenticated
  window.addEventListener('load', () => {
    if (frontendConfig.isAuthenticated() && !localStorage.getItem(frontendConfig.auth.userDataKey)) {
      frontendConfig.refreshUserDataFromAPI();
    }
  });
}

frontendConfig.validateConfig = () => {
  const required = [
    'REACT_APP_BASE_API'
  ];

  const missing = required.filter(key => !process.env[key]);
  
  if (missing.length > 0) {
    console.warn(`⚠️  Missing environment variables: ${missing.join(', ')}`);
  }

  return missing.length === 0;
};

export default frontendConfig; 